import { Link, useNavigate } from "react-router";
import { bookmarks } from "../../Config/bookmarks";
import { useAppDispatch } from "../../Redux/hooks";
import { initSearch, updateSearch } from "../../Redux/Reducers/searchSlice";
import {
  useFavoriteBookmarkMutation,
  useReadBookmarksQuery,
} from "../../Redux/Services/bookmarkApi";
import { InterfaceBookmark } from "../../Services/Serverless/Lookup/Bookmark/bookmark.schema";
import { WithTextId } from "../../Services/Serverless/Utils/withId.interface";
import { boroNameByCode } from "../../utils/boros/boros";
import Loading from "../Infrastructure/Loading";
import { useAuth0 } from "@auth0/auth0-react";
import isAdmin from "../Auth/isAdmin";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleLeft, faCircleRight } from "@fortawesome/free-solid-svg-icons";

export default function Bookmarks({
  token,
  paginate = false,
}: {
  token: string;
  paginate?: boolean;
}) {
  const { user } = useAuth0();
  const dispatch = useAppDispatch();
  const [favoriteBookmark, favoriteBookmarkResult] =
    useFavoriteBookmarkMutation();

  const pageSize = 10;
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching } = useReadBookmarksQuery({
    token,
    params: { skip: page * pageSize, limit: pageSize },
  });
  const navigate = useNavigate();
  const handleClick = (bookmark: WithTextId<InterfaceBookmark>) => {
    dispatch(
      updateSearch({
        ...initSearch.params,
        boro: bookmark.boro,
        houseNo: bookmark.houseNo,
        streetName: bookmark.streetName,
        aptNo: bookmark.aptNo || "",
      }),
    );
    if (
      bookmark.boro !== "" &&
      bookmark.houseNo !== "" &&
      bookmark.streetName !== ""
    ) {
      navigate("/property-search/candidates");
    }
  };
  enum pageDirection {
    newer,
    older,
  }
  const handlePage = (dir: pageDirection) => {
    if (dir === pageDirection.newer) {
      if (page > 0) setPage(page - 1);
    } else {
      setPage(page + 1);
    }
  };
  const handleFav = (id: string, fav: boolean) => {
    favoriteBookmark({
      token,
      data: { _id: id, favorite: fav },
    });
  };
  const isFavPending = (id: string) => {
    if (favoriteBookmarkResult.status === "pending") {
      if (favoriteBookmarkResult.originalArgs?.data._id === id) {
        return true;
      }
    }
    if (isFetching) {
      if (favoriteBookmarkResult.originalArgs?.data._id === id) {
        return true;
      }
    }
    return false;
  };
  if (isLoading) return <Loading />;
  return (
    <div className="bookmarks">
      {data && (data.history.length !== 0 || data.favorite.length !== 0) && (
        <>
          <ul>
            <RenderBookmarks
              bookmarks={data.favorite}
              handleClick={handleClick}
              handleFav={handleFav}
              isFavPending={isFavPending}
            />
            <RenderBookmarks
              bookmarks={data.history}
              handleClick={handleClick}
              handleFav={handleFav}
              isFavPending={isFavPending}
            />
          </ul>
          {paginate ? (
            <div className="pagination">
              <div>
                {page > 0 && (
                  <button
                    onClick={() => handlePage(pageDirection.newer)}
                    className="as-link"
                  >
                    <FontAwesomeIcon icon={faCircleLeft} /> Newer
                  </button>
                )}
              </div>
              <div>{page > 0 && `Page ${page + 1}`}</div>
              <div>
                {data.history.length >= pageSize && (
                  <button
                    className="as-link"
                    onClick={() => handlePage(pageDirection.older)}
                  >
                    Older <FontAwesomeIcon icon={faCircleRight} />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <Link to="/property-search/history">View All</Link>
          )}
        </>
      )}
      {isAdmin(user) && (
        <div>
          <h2>Admin Links</h2>
          <ul>
            {bookmarks.map((bookmark) => (
              <li key={bookmark.label}>
                <button
                  className="as-link"
                  onClick={() => {
                    dispatch(
                      updateSearch({
                        ...initSearch.params,
                        boro: bookmark.address.boro,
                        houseNo: bookmark.address.houseNo,
                        streetName: bookmark.address.streetName,
                      }),
                    );
                  }}
                >
                  {bookmark.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const RenderBookmarks = ({
  bookmarks,
  handleClick,
  handleFav,
  isFavPending,
}: {
  bookmarks: WithTextId<InterfaceBookmark>[];
  handleClick: (bookmark: WithTextId<InterfaceBookmark>) => void;
  handleFav: (id: string, fav: boolean) => void;
  isFavPending: (id: string) => boolean;
}) => {
  return (
    <>
      {bookmarks.map((bookmark) => (
        <li key={bookmark._id} className={bookmark.favorite ? "fav" : ""}>
          <button className="as-link" onClick={() => handleClick(bookmark)}>
            {bookmark.houseNo} {bookmark.streetName},{" "}
            {bookmark.aptNo && `APT ${bookmark.aptNo}, `}{" "}
            {boroNameByCode(bookmark.boro).toUpperCase()}
          </button>
          {isFavPending(bookmark._id) ? (
            <button className="as-link" disabled>
              ⧗...
            </button>
          ) : (
            <button
              className="as-link"
              onClick={() => handleFav(bookmark._id, !bookmark.favorite)}
            >
              {bookmark.favorite ? "★" : "☆"}
            </button>
          )}
        </li>
      ))}
    </>
  );
};
